import type { Messages } from './messages';

/**
 * Copy for `src/app/tenant/mail/`. Owned by that page's author, and by nobody else.
 *
 * Every key starts with `tenantMail.` — the compiler refuses anything else, so
 * this file cannot collide with another page's. Shared words (save, cancel,
 * retry, statuses, role names) are already in `common.ts` and
 * `vocabulary.ts`; use those rather than restating them here.
 */
export const tenantMail = {
  'tenantMail.routingSuperuserOnly':
    'تغيير مرسِل البريد ورابط الدعوات ورابط الواجهة يقتصر على المشرف الأعلى للمنصّة، لأن هذه الإعدادات تحدد وجهة روابط الدخول المرسلة إلى أعضاء المنشأة. يمكنك إرسال رسالة تجربة وتعديل القوالب.',
  'tenantMail.readOnly': 'رتبتك تسمح بعرض إعدادات البريد وقوالبه ومعاينتها فقط. التعديل وإرسال التجارب يحتاجان صلاحية «تعديل المنشآت».',

  /* Status summary */
  'tenantMail.status.title': 'حالة الإرسال',
  'tenantMail.status.description': 'من أين تخرج رسائل هذه المنشأة الآن، وماذا حدث لآخر رسالة.',
  'tenantMail.status.mode': 'طريقة الإرسال',
  'tenantMail.status.platform': 'مرحِّل المنصّة',
  'tenantMail.status.platformHint': 'لم تُضبط إعدادات خاصة؛ تصل الرسائل من عنوان المنصّة لا من نطاق المنشأة.',
  'tenantMail.status.own': 'مرحِّل المنشأة',
  'tenantMail.status.ownOff': 'مرحِّل المنشأة معطّل',
  'tenantMail.status.ownOffHint': 'الإعدادات محفوظة لكنها معطّلة؛ تُرسل الرسائل عبر مرحِّل المنصّة.',
  'tenantMail.status.server': 'الخادم',
  'tenantMail.status.security': 'التشفير',
  'tenantMail.status.sender': 'المرسِل',
  'tenantMail.status.username': 'اسم مستخدم المرحِّل',
  'tenantMail.status.noUsername': 'لا اسم مستخدم — يُرسل دون مصادقة',
  'tenantMail.status.password': 'كلمة مرور المرحِّل',
  'tenantMail.status.updated': 'آخر تعديل',
  'tenantMail.status.lastDispatch': 'آخر رسالة',
  'tenantMail.status.noDispatch': 'لم تُرسل رسائل بعد',
  'tenantMail.status.lastTest': 'تجربة هذه الجلسة',
  'tenantMail.status.testOk': 'قبِلها المرحِّل',
  'tenantMail.status.testFailed': 'فشلت',
  'tenantMail.status.notTested': 'لم تُجرَّب في هذه الجلسة',
  'tenantMail.status.linksDeployment': 'لا أصل محدَّد للمنشأة؛ راجع قسم «رابط الواجهة».',

  /* Sections */
  'tenantMail.sections': 'أقسام البريد',
  'tenantMail.section.settings': 'الإعدادات',
  'tenantMail.section.links': 'رابط الواجهة',
  'tenantMail.section.test': 'رسالة تجريبية',
  'tenantMail.section.dispatches': 'آخر الرسائل',
  'tenantMail.section.templates': 'القوالب',
  'tenantMail.section.unsaved': 'فيه تعديلات غير محفوظة',
  'tenantMail.section.failed': 'رسائل لم تُسلَّم بين المحمَّلة: {count}',

  /* Settings form */
  'tenantMail.form.title': 'إعدادات المرحِّل (SMTP)',
  'tenantMail.form.description': 'اختياري. بدونه تُرسل رسائل المنشأة عبر مرحِّل المنصّة. يُحفظ الإعداد كاملًا عند كل حفظ، ما عدا كلمة المرور.',
  'tenantMail.form.firstTime': 'هذه المنشأة تستخدم مرحِّل المنصّة. املأ الحقول واحفظ لترسل رسائلها من نطاقها الخاص.',
  'tenantMail.form.enabled': 'استخدام مرحِّل المنشأة',
  'tenantMail.form.enabledHint': 'عند التعطيل تبقى الإعدادات محفوظة وتعود الرسائل إلى مرحِّل المنصّة.',
  'tenantMail.form.host': 'خادم SMTP',
  'tenantMail.form.hostHint': 'اسم الخادم فقط، بلا بروتوكول.',
  'tenantMail.form.port': 'المنفذ',
  'tenantMail.form.portHint': '587 مع STARTTLS، و465 مع TLS المباشر.',
  'tenantMail.form.security': 'التشفير',
  'tenantMail.form.starttls': 'STARTTLS',
  'tenantMail.form.starttlsHint': 'يبدأ الاتصال عاديًا ثم يُشفَّر. المعتاد على المنفذ 587.',
  'tenantMail.form.tls': 'TLS مباشر',
  'tenantMail.form.tlsHint': 'مشفّر منذ الاتصال الأول. المعتاد على المنفذ 465.',
  'tenantMail.form.username': 'اسم المستخدم',
  'tenantMail.form.usernameHint': 'غالبًا عنوان البريد نفسه. اتركه فارغًا لمرحِّل لا يطلب دخولًا.',
  'tenantMail.form.password': 'كلمة المرور',
  'tenantMail.form.passwordHint': 'لا تُعرض أبدًا. اتركها كما هي للإبقاء على المحفوظة.',
  'tenantMail.form.passwordReplace': 'تتوقف كلمة المرور السابقة عن العمل فور الحفظ، وكل رسالة في الطابور تُرسل بالجديدة.',
  'tenantMail.form.fromAddress': 'عنوان المرسِل',
  'tenantMail.form.fromAddressHint': 'العنوان الذي يراه المستلم، على نطاق المنشأة. يجب أن يكون المرحِّل مخوَّلًا بالإرسال منه وإلا أُسقطت الرسائل بصمت.',
  'tenantMail.form.fromName': 'اسم المرسِل',
  'tenantMail.form.fromNameHint': 'يظهر بجانب العنوان في صندوق الوارد.',
  'tenantMail.form.linkBase': 'أصل روابط الرسائل',
  'tenantMail.form.linkBaseHint': 'أصل تطبيق المنشأة الذي تُبنى عليه روابط الدعوة واستعادة كلمة المرور، مثل \u2066https://app.example.sa\u2069 — بلا مسار. إن تُرك فارغًا يُستخدم «رابط الواجهة» من القسم الذي يحمل اسمه.',
  'tenantMail.form.discard': 'تجاهل التغييرات',
  'tenantMail.form.save': 'حفظ إعدادات البريد',
  'tenantMail.form.saved': 'حُفظت إعدادات البريد.',
  'tenantMail.form.invalid': 'أكمل الحقول المطلوبة وصحّح المعلَّمة قبل الحفظ.',
  'tenantMail.form.clean': 'لا تغييرات لحفظها.',

  /* Validation — mirrors `mailSettingsSchema` */
  'tenantMail.error.hostRequired': 'اكتب اسم خادم SMTP.',
  'tenantMail.error.hostShape': 'اسم الخادم فقط، بلا مسافات ولا «\u2066://\u2069».',
  'tenantMail.error.port': 'المنفذ رقم صحيح بين 1 و65535.',
  'tenantMail.error.fromRequired': 'اكتب عنوان المرسِل.',
  'tenantMail.error.email': 'هذا ليس عنوان بريد صالحًا.',
  'tenantMail.error.tooLong': 'النص أطول مما يقبله الخادم.',
  'tenantMail.error.url': 'هذا ليس رابطًا صالحًا. ابدأ بـ \u2066https://\u2069',
  'tenantMail.error.linkHttps': 'يجب أن يبدأ بـ \u2066https://\u2069 — الرابط يحمل رمز دخول لمرة واحدة. http مسموح لـ localhost فقط.',
  'tenantMail.error.linkPath': 'الأصل فقط بلا مسار أو استعلام: يُلحق الرمز به، والمسار يُنتج روابط لا تفتح شيئًا.',
  'tenantMail.error.frontendRequired': 'اكتب الرابط، أو استخدم «إزالة» للعودة إلى الافتراضي.',
  'tenantMail.warn.tlsOn587': 'TLS المباشر على المنفذ 587 غير معتاد؛ أغلب المزوّدين يتوقعون STARTTLS عليه. إن انتهت التجربة بانقضاء المهلة فابدأ من هنا.',
  'tenantMail.warn.starttlsOn465': 'STARTTLS على المنفذ 465 غير معتاد؛ أغلب المزوّدين يتوقعون TLS المباشر عليه. إن انتهت التجربة بانقضاء المهلة فابدأ من هنا.',
  'tenantMail.warn.usernameNoPassword': 'اسم مستخدم بلا كلمة مرور محفوظة: أغلب المرحِّلات ترفض الدخول هكذا.',

  /* Guided test */
  'tenantMail.test.title': 'إرسال رسالة تجريبية',
  'tenantMail.test.description': 'رسالة حقيقية عبر الإعدادات المحفوظة، بالمسار نفسه الذي تسلكه الدعوات.',
  'tenantMail.test.step1': 'احفظ الإعدادات أولًا — التجربة تستخدم المحفوظ لا ما في النموذج.',
  'tenantMail.test.step2': 'أرسل إلى عنوان تستطيع فتحه أنت أو العميل الآن.',
  'tenantMail.test.step3': 'اقرأ النتيجة: قبول المرحِّل لا يعني الوصول؛ إن لم تصل فابحث بمعرّف الرسالة في سجل المزوّد.',
  'tenantMail.test.to': 'المستلم',
  'tenantMail.test.toHint': 'عنوان يستطيع أحد فتحه الآن. لا افتراضي له عمدًا.',
  'tenantMail.test.send': 'إرسال التجربة',
  'tenantMail.test.needsSettings': 'لا يوجد ما يُجرَّب: هذه المنشأة تستخدم مرحِّل المنصّة. احفظ إعدادات خاصة أولًا.',
  'tenantMail.test.needsEnabled': 'مرحِّل المنشأة معطّل؛ فعّله واحفظ قبل التجربة.',
  'tenantMail.test.dirty': 'لديك تغييرات غير محفوظة. التجربة ستستخدم الإعدادات المحفوظة لا التي في النموذج.',
  'tenantMail.test.okTitle': 'قبِل المرحِّل الرسالة',
  'tenantMail.test.okBody': 'أُرسلت عبر {via}. هذا لا يعني أنها وصلت: إن لم تظهر في الوارد أو البريد غير المرغوب فابحث بمعرّف الرسالة في سجل نشاط المزوّد.',
  'tenantMail.test.failTitle': 'لم تُرسل الرسالة',
  'tenantMail.test.failBody': 'المحاولة عبر {via}. نص الخطأ كما أعاده المرحِّل:',
  'tenantMail.test.messageId': 'معرّف الرسالة',
  'tenantMail.test.error': 'نص الخطأ',
  'tenantMail.test.unavailable': 'تعذّر فك كلمة المرور المحفوظة. أعد إدخالها واحفظ — حتى ذلك الحين لا تُرسل هذه المنشأة أي رسالة.',

  /* Frontend URL */
  'tenantMail.frontend.title': 'رابط الواجهة',
  'tenantMail.frontend.description':
    'عنوان تطبيق المنشأة نفسها. عليه تُبنى روابط الدعوة واستعادة كلمة المرور ما لم يُحدَّد «أصل روابط الرسائل» في إعدادات المرحِّل، ويُقبل أصله في CORS لمسارات هذه المنشأة. ليس عنوان العودة بعد الدخول عبر Microsoft: ذلك يحدّده النشر، ويظهر في تبويب «الدخول عبر Microsoft».',
  'tenantMail.frontend.label': 'رابط الواجهة',
  'tenantMail.frontend.hint': 'عنوان http أو https، مثل \u2066https://app.example.sa\u2069. من يغيّره يغيّر أين يُرسَل العضو ليختار كلمة مروره.',
  'tenantMail.frontend.save': 'حفظ الرابط',
  'tenantMail.frontend.saved': 'حُفظ رابط الواجهة.',
  'tenantMail.frontend.clear': 'إزالة',
  'tenantMail.frontend.cleared': 'أُزيل رابط الواجهة.',
  'tenantMail.frontend.notHttps': 'رابط بلا https: المتصفحات تحذّر منه، وروابط الدعوة المبنية عليه تحمل رمز دخول بلا تشفير.',
  'tenantMail.frontend.clearTitle': 'إزالة رابط الواجهة؟',
  'tenantMail.frontend.clearConsequence': 'تُبنى روابط الدعوات واستعادة كلمة المرور الجديدة على عنوان النشر الافتراضي ما لم يُحدَّد أصل في إعدادات البريد، ولا يعود أصل هذا الرابط مقبولًا في CORS لمسارات المنشأة. الروابط المرسلة سابقًا لا تتغير.',
  'tenantMail.frontend.clearAffected': 'كل أعضاء المنشأة ومن يُدعى إليها بعد الآن.',
  'tenantMail.links.label': 'روابط الرسائل تُبنى الآن على',
  'tenantMail.links.fromMail': 'أصل روابط الرسائل في إعدادات المرحِّل',
  'tenantMail.links.fromFrontend': 'رابط الواجهة',
  'tenantMail.links.deployment': 'العنوان الافتراضي للنشر',
  'tenantMail.links.deploymentHint': 'لا هذه ولا تلك محدَّدة، فتُستخدم قيمة النشر العامة التي لا تعرضها أي واجهة. على نشر يخدم أكثر من منشأة لا تكون صحيحة للجميع: الرسالة تصل وزرّها لا يفتح شيئًا.',

  /* Dispatches */
  'tenantMail.log.title': 'الرسائل المرسلة',
  'tenantMail.log.description': 'كل دعوة واستعادة كلمة مرور وقرار انضمام أُرسل لهذه المنشأة، الأحدث أولًا. «قبِلها المُرحِّل» تعني أن المرحِّل قال نعم، لا أن الرسالة وصلت.',
  'tenantMail.log.caption': 'الرسائل المرسلة لهذه المنشأة',
  'tenantMail.log.filter': 'الحالة',
  'tenantMail.log.all': 'الكل',
  'tenantMail.log.refresh': 'تحديث',
  'tenantMail.log.when': 'الوقت',
  'tenantMail.log.queuedAt': 'دخل الطابور {time}',
  'tenantMail.log.template': 'القالب',
  'tenantMail.log.recipient': 'المستلم',
  'tenantMail.log.status': 'الحالة',
  'tenantMail.log.relay': 'المرحِّل ومعرّف الرسالة',
  'tenantMail.log.reason': 'سبب الفشل',
  'tenantMail.log.attempts': '{count} محاولات',
  'tenantMail.log.viaUnknown': 'غير مسجَّل',
  'tenantMail.log.emptyTitle': 'لم تُرسل هذه المنشأة أي رسالة بعد',
  'tenantMail.log.emptyBody': 'تظهر هنا الدعوات ورسائل الاستعادة عند إرسالها. لاختبار الإعدادات الآن افتح قسم «رسالة تجريبية» — التجارب لا تُسجَّل هنا.',
  'tenantMail.log.noMatchTitle': 'لا رسائل بهذه الحالة',
  'tenantMail.log.noMatchBody': 'لم تُسجَّل لهذه المنشأة رسالة بهذه الحالة. اختر «الكل» لترى كل ما أُرسل.',
  'tenantMail.log.loadOlder': 'تحميل رسائل أقدم',
  'tenantMail.log.moreFailed': 'تعذّر تحميل المزيد:',

  /* Dispatch purposes — `InvitationPurpose`, as the SQL returns it */
  'tenantMail.purpose.INVITATION': 'دعوة تفعيل الحساب',
  'tenantMail.purpose.PASSWORD_RESET': 'إعادة تعيين كلمة المرور',
  'tenantMail.purpose.JOIN_APPROVED': 'الموافقة على طلب الانضمام',
  'tenantMail.purpose.JOIN_REJECTED': 'عدم الموافقة على طلب الانضمام',


  /* ── Templates section (was «قوالب البريد»; merged 2026-10-08) ─────────── */


  /* What exists, and who gets it */
  'tenantMail.tpl.catalogue.body': '{count} قالبًا للمنشأة: 21 من التصميم موزّعة على ثلاث جهات مستلِمة، والباقي مسوّدات إضافية. {unwired} منها لا يرسلها النظام بعد؛ نصها جاهز ويُستخدم حين تُربط.',
  'tenantMail.tpl.groups.label': 'الجهة المستلِمة',
  'tenantMail.tpl.recipient.user': 'المستخدم',
  'tenantMail.tpl.recipient.model_supervisor': 'مشرف النماذج',
  'tenantMail.tpl.recipient.general_admin': 'مشرف عام',

  /* The list */
  'tenantMail.tpl.list.title': 'القوالب',
  'tenantMail.tpl.list.label': 'قوالب البريد للجهة المختارة',
  'tenantMail.tpl.list.trigger': 'يُرسل عند: {trigger}',
  'tenantMail.tpl.list.drafted': 'مسوّدة',
  'tenantMail.tpl.list.groupEmpty': 'لا قوالب لهذه الجهة.',
  'tenantMail.tpl.list.customised': 'مخصّص',
  'tenantMail.tpl.list.default': 'افتراضي',
  'tenantMail.tpl.list.notWired': 'لا يُرسل بعد',
  'tenantMail.tpl.list.unsaved': 'غير محفوظ',
  'tenantMail.tpl.list.emptyTitle': 'لا قوالب',
  'tenantMail.tpl.list.emptyBody': 'لم يُعد الخادم أي قالب لهذه المنشأة. أعد التحميل، وإن تكرر فالكتالوج على الخادم فارغ.',

  /* Mode switch */
  'tenantMail.tpl.mode.label': 'ما تعدّله',
  'tenantMail.tpl.mode.text': 'نص القالب',
  'tenantMail.tpl.mode.branding': 'هوية البريد',

  /* Editor */
  'tenantMail.tpl.editor.trigger': 'يُرسل عندما: {trigger}',
  'tenantMail.tpl.editor.notWired': 'النص جاهز لكن لا شيء في النظام يرسل هذا القالب بعد. ما تحفظه هنا يُستخدم حين يُربط.',
  'tenantMail.tpl.editor.drafted': 'نص هذا القالب صاغه الفريق ولم يرد في التصميم، فلا يُحسب ضمن الـ21 وينتظر الاعتماد.',
  'tenantMail.tpl.editor.language': 'القالب بالعربية: ما تكتبه هنا يُرسل كما هو لكل المستلمين. الخادم لا يحفظ نسخة إنجليزية بعد، فلا حقل لها.',
  'tenantMail.tpl.editor.blankMeansDefault': 'الحقل الفارغ يعني نص المنصّة — النص الرمادي داخل الحقل هو ما سيُرسل.',
  'tenantMail.tpl.editor.subject': 'سطر الموضوع',
  'tenantMail.tpl.editor.preheader': 'سطر المعاينة',
  'tenantMail.tpl.editor.preheaderHint': 'السطر الذي يظهر بعد الموضوع في قائمة الوارد ولا يظهر داخل الرسالة.',
  'tenantMail.tpl.editor.body': 'نص الرسالة',
  'tenantMail.tpl.editor.bodyHint': 'كل سطر فقرة. حتى 12 فقرة. إن كتبت هنا شيئًا حلّ محل فقرات المنصّة كلها لا بعضها.',
  'tenantMail.tpl.editor.cta': 'نص الزر',
  'tenantMail.tpl.editor.ctaHint': 'نص الزر فقط؛ وجهته يحددها النظام ولا تُعدَّل من هنا.',
  'tenantMail.tpl.editor.count': '{count} / {max}',
  'tenantMail.tpl.editor.literal': 'سيظهر «{tokens}» للمستلم حرفيًا: القوالب لا تدعم متغيّرات، والنص يُرسل كما يُكتب.',
  'tenantMail.tpl.editor.save': 'حفظ القالب',
  'tenantMail.tpl.editor.saved': 'حُفظ القالب «{label}».',
  'tenantMail.tpl.editor.discard': 'تجاهل التغييرات',
  'tenantMail.tpl.editor.reset': 'استعادة نص المنصّة',
  'tenantMail.tpl.editor.clean': 'لا تغييرات لحفظها.',
  'tenantMail.tpl.editor.invalid': 'صحّح الحقول المعلَّمة قبل الحفظ.',
  'tenantMail.tpl.reset.title': 'استعادة نص المنصّة لـ«{label}»؟',
  'tenantMail.tpl.reset.consequence': 'يُحذف النص المخصّص لهذا القالب، وتصل الرسالة بنص المنصّة من الإرسال التالي. الرسائل المرسلة سابقًا لا تتغير.',
  'tenantMail.tpl.reset.affected': 'كل من يتلقى «{label}» من هذه المنشأة.',
  'tenantMail.tpl.reset.done': 'عاد «{label}» إلى نص المنصّة.',

  /* What the server fills in */
  'tenantMail.tpl.filled.title': 'ما يملؤه النظام تلقائيًا',
  'tenantMail.tpl.filled.body': 'لا تُكتب هذه في النص ولا تُدرج فيه؛ يضيفها الخادم لكل رسالة.',
  'tenantMail.tpl.filled.greeting': 'التحية',
  'tenantMail.tpl.filled.greetingHint': '«مرحبًا» ثم اسم المستلم إن كان عضوًا معروفًا، قبل نصك.',
  'tenantMail.tpl.filled.tenant': 'اسم المنشأة',
  'tenantMail.tpl.filled.tenantHint': 'في سطر التذييل ما لم تضع تذييلًا خاصًا في «هوية البريد».',
  'tenantMail.tpl.filled.link': 'رابط الزر',
  'tenantMail.tpl.filled.linkHint': 'يبنيه النظام لكل مستلم، ويحمل رمزًا لمرة واحدة حيث يلزم.',
  'tenantMail.tpl.filled.notes': 'سطر الصلاحية',
  'tenantMail.tpl.filled.notesHint': 'مدة صلاحية الرابط تُحسب عند الإرسال وتُضاف تحت النص.',

  /* Preview */
  'tenantMail.tpl.preview.title': 'المعاينة',
  'tenantMail.tpl.preview.description': 'كما يرسمها الخادم من المسودة غير المحفوظة، ببيانات نموذجية لا تخص أحدًا.',
  'tenantMail.tpl.preview.frame': 'معاينة رسالة «{label}»',
  'tenantMail.tpl.preview.from': 'من',
  'tenantMail.tpl.preview.fromUnknown': 'غير معروف',
  'tenantMail.tpl.preview.subject': 'الموضوع',
  'tenantMail.tpl.preview.width': 'عرض المعاينة',
  'tenantMail.tpl.preview.desktop': 'شاشة',
  'tenantMail.tpl.preview.mobile': 'هاتف',
  'tenantMail.tpl.preview.updating': 'تُحدَّث المعاينة…',
  'tenantMail.tpl.preview.stale': 'تعذّر تحديث المعاينة؛ المعروض أقدم من المسودة.',
  'tenantMail.tpl.preview.blocked': 'صحّح الحقول المعلَّمة لتُحدَّث المعاينة.',
  'tenantMail.tpl.preview.pick': 'اختر قالبًا من القائمة لمعاينته.',

  /* Test */
  'tenantMail.tpl.test.title': 'أرسل لي تجربة',
  'tenantMail.tpl.test.hint': 'رسالة حقيقية بالمسودة الحالية والهوية الحالية، عبر مرحِّل المنشأة إن كان مفعّلًا.',
  'tenantMail.tpl.test.send': 'إرسال التجربة',
  'tenantMail.tpl.test.okTitle': 'قبِل المرحِّل الرسالة',
  'tenantMail.tpl.test.okBody': 'أُرسلت عبر {via} إلى {to}. القبول لا يعني الوصول؛ إن لم تظهر فتحقق من البريد غير المرغوب.',
  'tenantMail.tpl.test.failTitle': 'لم تُرسل الرسالة',
  'tenantMail.tpl.test.failBody': 'المحاولة عبر {via}. نص الخطأ كما أعاده المرحِّل:',
  'tenantMail.tpl.test.error': 'نص الخطأ',

  /* Branding */
  'tenantMail.tpl.branding.title': 'هوية البريد',
  'tenantMail.tpl.branding.description': 'شكل كل رسائل هذه المنشأة: الشعار والألوان والتذييل. تظهر في المعاينة فور التعديل، وتُطبَّق على الرسائل بعد الحفظ.',
  'tenantMail.tpl.branding.platform':
    'لا هوية بريد خاصة بهذه المنشأة: تتبع رسائلها المظهر الافتراضي للمنصّة. حفظ أي حقل هنا يجعل لها هوية خاصة تحلّ محلّ الافتراضي كاملًا، وما تتركه فارغًا عندها يأخذ المظهر المدمج لا الافتراضي.',
  'tenantMail.tpl.branding.platformOpen': 'المظهر الافتراضي',
  'tenantMail.tpl.branding.brandColor': 'لون العلامة',
  'tenantMail.tpl.branding.brandColorHint': 'خلفية الزر.',
  'tenantMail.tpl.branding.headerColor': 'لون الترويسة',
  'tenantMail.tpl.branding.headerColorHint': 'كلمة «أيد» في الترويسة وعنوان الرسالة. الترويسة بيضاء، فاختر لونًا داكنًا.',
  'tenantMail.tpl.branding.buttonTextColor': 'لون نص الزر',
  'tenantMail.tpl.branding.buttonTextColorHint': 'يجب أن يُقرأ فوق لون العلامة.',
  'tenantMail.tpl.branding.pick': 'اختيار {field}',
  'tenantMail.tpl.branding.useDefault': 'اللون المدمج',
  'tenantMail.tpl.branding.defaultIs': 'فارغ = لون المظهر المدمج في المنصّة.',
  'tenantMail.tpl.branding.footerTitle': 'عنوان التذييل',
  'tenantMail.tpl.branding.footerTitleHint': 'السطر الصغير تحت البطاقة.',
  'tenantMail.tpl.branding.footerNote': 'ملاحظة التذييل',
  'tenantMail.tpl.branding.footerNoteHint': 'السطر داخل البطاقة تحت الفاصل. فارغ = ملاحظة المنصّة عن العنوان الذي لا يستقبل الردود.',
  'tenantMail.tpl.branding.save': 'حفظ الهوية',
  'tenantMail.tpl.branding.saved': 'حُفظت هوية البريد.',
  'tenantMail.tpl.branding.discard': 'تجاهل التغييرات',
  'tenantMail.tpl.branding.reset': 'العودة إلى المظهر الافتراضي',
  'tenantMail.tpl.branding.resetTitle': 'العودة إلى المظهر الافتراضي للمنصّة؟',
  'tenantMail.tpl.branding.resetConsequence': 'تُحذف الألوان والتذييل والشعار الخاصة بهذه المنشأة، وتصل رسائلها كلها بالمظهر الافتراضي للمنصّة من الإرسال التالي. نصوص القوالب المخصّصة لا تتأثر.',
  'tenantMail.tpl.branding.resetAffected': 'كل من يتلقى بريدًا من هذه المنشأة.',
  'tenantMail.tpl.branding.resetDone': 'عادت الرسائل إلى المظهر الافتراضي للمنصّة.',
  'tenantMail.tpl.contrast.button': 'نص الزر فوق لون العلامة تباينه {ratio}:1 — أقل من 4.5:1، فيصعب قراءته.',
  'tenantMail.tpl.contrast.header': 'عنوان الرسالة وكلمة «أيد» بهذا اللون فوق الخلفية البيضاء تباينهما {ratio}:1 — أقل من 4.5:1. اختر لونًا أغمق.',

  /* Logo */
  'tenantMail.tpl.logo.label': 'الشعار',
  'tenantMail.tpl.logo.hint': 'PNG أو JPEG أو GIF، حتى ٣ ميغابايت، ويُفضَّل بارتفاع 56 بكسل على الأقل. يُطبَّق فور رفعه، بلا زر حفظ.',
  'tenantMail.tpl.logo.none': 'لا شعار — تظهر كلمة «أيد» في الترويسة.',
  'tenantMail.tpl.logo.alt': 'شعار المنشأة الحالي',
  'tenantMail.tpl.logo.upload': 'رفع شعار',
  'tenantMail.tpl.logo.replace': 'استبدال الشعار',
  'tenantMail.tpl.logo.remove': 'إزالة الشعار',
  'tenantMail.tpl.logo.hashing': 'تجهيز الملف…',
  'tenantMail.tpl.logo.uploading': 'جارٍ الرفع… {percent}',
  'tenantMail.tpl.logo.applying': 'جارٍ تطبيق الشعار…',
  'tenantMail.tpl.logo.saved': 'حُفظ الشعار.',
  'tenantMail.tpl.logo.reused': 'هذه الصورة محفوظة مسبقًا لدى المنشأة؛ طُبّقت دون رفع.',
  'tenantMail.tpl.logo.removed': 'أُزيل الشعار.',
  'tenantMail.tpl.logo.type': 'الشعار يجب أن يكون PNG أو JPEG أو GIF. ملفات SVG وWebP لا تقبلها برامج البريد كلها.',
  'tenantMail.tpl.logo.size': 'الملف أكبر من ٣ ميغابايت. صغّره وأعد المحاولة.',
  'tenantMail.tpl.logo.empty': 'الملف فارغ.',
  'tenantMail.tpl.logo.removeTitle': 'إزالة الشعار؟',
  'tenantMail.tpl.logo.removeConsequence': 'تعود الترويسة إلى كلمة «أيد» في كل رسالة تُرسل بعد الآن. الرسائل المرسلة سابقًا تبقى تشير إلى الصورة القديمة.',
  'tenantMail.tpl.logo.removeAffected': 'كل من يتلقى بريدًا من هذه المنشأة.',

  /* Validation — mirrors emailTemplateSchema / emailBrandingSchema */
  'tenantMail.tpl.error.subjectLong': 'الموضوع أطول من 200 حرف.',
  'tenantMail.tpl.error.preheaderLong': 'سطر المعاينة أطول من 200 حرف.',
  'tenantMail.tpl.error.tooManyParagraphs': 'أكثر من 12 فقرة. ادمج بعض الأسطر.',
  'tenantMail.tpl.error.paragraphLong': 'إحدى الفقرات أطول من 2000 حرف.',
  'tenantMail.tpl.error.ctaLong': 'نص الزر أطول من 80 حرفًا.',
  'tenantMail.tpl.error.hex': 'اللون ثلاث أو ست خانات ست عشرية، مثل \u2066#00587C\u2069.',
  'tenantMail.tpl.error.footerTitleLong': 'عنوان التذييل أطول من 200 حرف.',
  'tenantMail.tpl.error.footerNoteLong': 'ملاحظة التذييل أطول من 400 حرف.',
  'tenantMail.tpl.error.logoHttps': 'رابط الشعار المحفوظ ليس https؛ ارفع الشعار من جديد.',
  'tenantMail.tpl.error.email': 'هذا ليس عنوان بريد صالحًا.',

  /* Switching template over unsaved text */
  'tenantMail.tpl.switch.title': 'تجاهل تعديلات هذا القالب؟',
  'tenantMail.tpl.switch.consequence': 'لم يُحفظ نص القالب الذي تعدّله. إن انتقلت إلى قالب آخر ضاع، ويبقى القالب على المحفوظ. تعديلات هوية البريد تبقى كما هي.',
  'tenantMail.tpl.switch.confirm': 'تجاهل وانتقال',

  /* Unsaved guard */
  'tenantMail.leave.title': 'تجاهل التغييرات غير المحفوظة؟',
  'tenantMail.leave.consequence':
    'لم تُحفظ تعديلاتك في البريد — الإعدادات أو رابط الواجهة أو نص قالب أو هوية البريد. إن غادرت الآن ضاعت، وتبقى المنشأة على المحفوظ.',
  'tenantMail.leave.confirm': 'تجاهل ومغادرة',
} as const satisfies Messages<'tenantMail'>;
